/**
 * Simplified Navigation Manager
 *
 * Header tab  – flat list of nav items (with optional dropdown children).
 *               CMS page slugs available as a picker when adding items.
 *               Custom HTML editor at the bottom to override the entire header.
 *
 * Footer tab  – columns (navGroups with placement='footer') each containing
 *               nav items. CMS page slugs picker + custom HTML editor.
 */

import { useState, useEffect, useCallback } from 'react';
import Editor from '@monaco-editor/react';
import {
  Plus,
  Pencil,
  Trash2,
  ChevronUp,
  ChevronDown,
  Eye,
  EyeOff,
  Loader2,
  Save,
  Code2,
  Link2,
  ChevronRight,
  LayoutTemplate,
  Columns,
  AlertTriangle,
  X,
  Type,
  FileCode,
} from 'lucide-react';
import {
  type NavItem,
  type NavItemPayload,
  type NavPlacement,
  type NavGroup,
  type NavGroupPayload,
  getAllNavItems,
  getAllNavGroups,
  createNavItem,
  updateNavItem,
  deleteNavItem,
  reorderNavItems,
  createNavGroup,
  updateNavGroup,
  deleteNavGroup,
  reorderNavGroups,
  getAllCMSPages,
  getHeaderHtml,
  saveHeaderHtml,
  getFooterHtml,
  saveFooterHtml,
  type CMSPage,
} from '@/lib/cms';
import { NAV_ICON_MAP, NavIconRenderer, type NavIconName } from '@/lib/nav-icons';
import { AdminCmsPlaceholderGuide } from './AdminCmsPlaceholderGuide';

interface AdminNavigationProps {
  currentUserId: string;
  showNotify?: (message: string, type?: 'success' | 'error' | 'info') => void;
  confirmAction?: (title: string, message: string, action: () => void) => void;
  /** Optional action (e.g. Help & guides) to show in the title row. */
  titleAction?: React.ReactNode;
}

// ── Nav item form state ───────────────────────────────────────────────────────

type ItemFormType = 'link' | 'text' | 'html';

interface ItemForm {
  itemType: ItemFormType;
  label: string;
  urlMode: 'cms' | 'custom';
  cmsSlug: string;
  customUrl: string;
  target: '_self' | '_blank';
  isVisible: boolean;
  icon: string;
  htmlContent: string;
}

const emptyItemForm = (): ItemForm => ({
  itemType: 'link',
  label: '',
  urlMode: 'cms',
  cmsSlug: '',
  customUrl: '',
  target: '_self',
  isVisible: true,
  icon: '',
  htmlContent: '',
});

// All available icon names for the picker
const ICON_NAMES = Object.keys(NAV_ICON_MAP) as NavIconName[];

// ── Component ─────────────────────────────────────────────────────────────────

export const AdminNavigation = ({
  currentUserId,
  showNotify,
  confirmAction,
  titleAction,
}: AdminNavigationProps) => {
  const [placement, setPlacement] = useState<NavPlacement>('header');

  // Data
  const [items, setItems] = useState<NavItem[]>([]);
  const [groups, setGroups] = useState<NavGroup[]>([]);
  const [cmsPages, setCmsPages] = useState<CMSPage[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  // HTML editors
  const [headerHtml, setHeaderHtml] = useState<string>('');
  const [footerHtml, setFooterHtml] = useState<string>('');
  const [htmlSaving, setHtmlSaving] = useState(false);
  const [activeHtmlTab, setActiveHtmlTab] = useState<NavPlacement | null>(null);

  // Nav item form
  const [itemFormOpen, setItemFormOpen] = useState(false);
  const [editingItem, setEditingItem] = useState<NavItem | null>(null);
  const [itemForm, setItemForm] = useState<ItemForm>(emptyItemForm());
  const [itemFormGroupId, setItemFormGroupId] = useState<string | null>(null);
  const [itemFormParentId, setItemFormParentId] = useState<string | null>(null);

  // Footer column form
  const [columnFormOpen, setColumnFormOpen] = useState(false);
  const [editingColumn, setEditingColumn] = useState<NavGroup | null>(null);
  const [columnName, setColumnName] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [allItems, allGroups, pages, hHtml, fHtml] = await Promise.all([
        getAllNavItems(),
        getAllNavGroups(),
        getAllCMSPages(),
        getHeaderHtml(),
        getFooterHtml(),
      ]);
      setItems(allItems);
      setGroups(allGroups);
      setCmsPages(pages.filter((p) => p.status === 'published' && p.slug));
      setHeaderHtml(hHtml ?? '');
      setFooterHtml(fHtml ?? '');
    } catch (err) {
      console.error('[AdminNavigation] Failed to load data:', err);
      showNotify?.('Failed to load navigation data', 'error');
    } finally {
      setLoading(false);
    }
  }, [showNotify]);

  useEffect(() => {
    load();
  }, [load]);

  // ── Derived lists ─────────────────────────────────────────────────────────

  const headerTopItems = items
    .filter((it) => it.placement === 'header' && !it.parentId)
    .sort((a, b) => a.order - b.order);

  const footerColumns = groups
    .filter((g) => g.placement === 'footer')
    .sort((a, b) => a.order - b.order);

  const childrenOf = (parentId: string) =>
    items.filter((it) => it.parentId === parentId).sort((a, b) => a.order - b.order);

  const columnItems = (groupId: string) =>
    items.filter((it) => it.groupId === groupId && !it.parentId).sort((a, b) => a.order - b.order);

  // ── Item CRUD ──────────────────────────────────────────────────────────────

  const openAddItem = (groupId: string | null = null, parentId: string | null = null) => {
    setEditingItem(null);
    setItemFormGroupId(groupId);
    setItemFormParentId(parentId);
    setItemForm(emptyItemForm());
    setItemFormOpen(true);
  };

  const openEditItem = (item: NavItem) => {
    setEditingItem(item);
    setItemFormGroupId(item.groupId ?? null);
    setItemFormParentId(item.parentId ?? null);

    const isSlug = item.slug != null && item.slug !== '';
    const derivedType: ItemFormType =
      item.type === 'text' ? 'text' :
      item.type === 'html' ? 'html' :
      'link';
    setItemForm({
      itemType: derivedType,
      label: item.label,
      urlMode: isSlug ? 'cms' : 'custom',
      cmsSlug: item.slug ?? '',
      customUrl: item.url ?? '',
      target: item.target ?? '_self',
      isVisible: item.isVisible,
      icon: item.icon ?? '',
      htmlContent: item.embedCode ?? '',
    });
    setItemFormOpen(true);
  };

  const handleItemSave = async () => {
    if (!itemForm.label.trim() && itemForm.itemType !== 'html') {
      showNotify?.('Label is required', 'error');
      return;
    }
    if (itemForm.itemType === 'html' && !itemForm.htmlContent.trim()) {
      showNotify?.('HTML content is required for HTML snippet items', 'error');
      return;
    }
    setSaving(true);
    try {
      const isLink = itemForm.itemType === 'link';
      const resolvedUrl = isLink && itemForm.urlMode === 'custom' ? (itemForm.customUrl.trim() || null) : null;
      const resolvedSlug = isLink && itemForm.urlMode === 'cms' ? (itemForm.cmsSlug.trim() || null) : null;

      const group = itemFormGroupId ? groups.find((g) => g.id === itemFormGroupId) : null;

      const payload: NavItemPayload = {
        label: itemForm.label.trim(),
        type: itemForm.itemType,
        url: resolvedUrl,
        slug: resolvedSlug,
        target: isLink ? itemForm.target : null,
        buttonStyle: null,
        embedCode: itemForm.itemType === 'html' ? itemForm.htmlContent : null,
        parentId: itemFormParentId ?? null,
        groupId: itemFormGroupId ?? null,
        placement: group?.placement ?? placement,
        isVisible: itemForm.isVisible,
        icon: itemForm.itemType !== 'html' ? (itemForm.icon.trim() || null) : null,
      };

      if (editingItem) {
        await updateNavItem(editingItem.id, payload, currentUserId);
        showNotify?.('Item updated', 'success');
      } else {
        const siblings = itemFormParentId
          ? childrenOf(itemFormParentId)
          : itemFormGroupId
          ? columnItems(itemFormGroupId)
          : headerTopItems;
        const maxOrder = siblings.length > 0 ? Math.max(...siblings.map((it) => it.order)) + 1 : 0;
        await createNavItem({ ...payload, order: maxOrder }, currentUserId);
        showNotify?.('Item added', 'success');
      }
      setItemFormOpen(false);
      await load();
    } catch (err) {
      console.error('[AdminNavigation] Failed to save item:', err);
      showNotify?.('Failed to save item', 'error');
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteItem = (item: NavItem) => {
    const children = childrenOf(item.id);
    const msg =
      children.length > 0
        ? `Delete "${item.label}"? This will also delete ${children.length} dropdown sub-item(s).`
        : `Delete "${item.label}"?`;
    const doDelete = async () => {
      setSaving(true);
      try {
        await Promise.all(children.map((c) => deleteNavItem(c.id)));
        await deleteNavItem(item.id);
        showNotify?.('Item deleted', 'success');
        await load();
      } catch (err) {
        console.error('[AdminNavigation] Failed to delete item:', err);
        showNotify?.('Failed to delete item', 'error');
      } finally {
        setSaving(false);
      }
    };
    if (confirmAction) {
      confirmAction('Delete Item', msg, doDelete);
    } else {
      if (window.confirm(msg)) doDelete();
    }
  };

  const moveItem = async (item: NavItem, direction: 'up' | 'down') => {
    const siblings = item.parentId
      ? childrenOf(item.parentId)
      : item.groupId
      ? columnItems(item.groupId)
      : headerTopItems;
    const sorted = [...siblings].sort((a, b) => a.order - b.order);
    const idx = sorted.findIndex((it) => it.id === item.id);
    const swapIdx = direction === 'up' ? idx - 1 : idx + 1;
    if (swapIdx < 0 || swapIdx >= sorted.length) return;
    const ids = sorted.map((it) => it.id);
    [ids[idx], ids[swapIdx]] = [ids[swapIdx], ids[idx]];
    setSaving(true);
    try {
      await reorderNavItems(ids, currentUserId);
      await load();
    } finally {
      setSaving(false);
    }
  };

  const toggleItemVisibility = async (item: NavItem) => {
    setSaving(true);
    try {
      await updateNavItem(item.id, { isVisible: !item.isVisible }, currentUserId);
      await load();
    } finally {
      setSaving(false);
    }
  };

  // ── Footer column CRUD ─────────────────────────────────────────────────────

  const openAddColumn = () => {
    setEditingColumn(null);
    setColumnName('');
    setColumnFormOpen(true);
  };

  const openEditColumn = (group: NavGroup) => {
    setEditingColumn(group);
    setColumnName(group.name);
    setColumnFormOpen(true);
  };

  const handleColumnSave = async () => {
    if (!columnName.trim()) {
      showNotify?.('Column name is required', 'error');
      return;
    }
    setSaving(true);
    try {
      const payload: NavGroupPayload = {
        name: columnName.trim(),
        placement: 'footer',
        isVisible: true,
        description: null,
      };
      if (editingColumn) {
        await updateNavGroup(editingColumn.id, payload, currentUserId);
        showNotify?.('Column updated', 'success');
      } else {
        const maxOrder = footerColumns.length > 0 ? Math.max(...footerColumns.map((g) => g.order)) + 1 : 0;
        await createNavGroup({ ...payload, order: maxOrder }, currentUserId);
        showNotify?.('Column created', 'success');
      }
      setColumnFormOpen(false);
      await load();
    } catch (err) {
      console.error('[AdminNavigation] Failed to save column:', err);
      showNotify?.('Failed to save column', 'error');
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteColumn = (group: NavGroup) => {
    const count = columnItems(group.id).length;
    const msg = count > 0
      ? `Delete column "${group.name}"? This will also delete all ${count} link(s) inside it.`
      : `Delete column "${group.name}"?`;
    const doDelete = async () => {
      setSaving(true);
      try {
        await Promise.all(columnItems(group.id).map((it) => deleteNavItem(it.id)));
        await deleteNavGroup(group.id);
        showNotify?.('Column deleted', 'success');
        await load();
      } catch (err) {
        console.error('[AdminNavigation] Failed to delete column:', err);
        showNotify?.('Failed to delete column', 'error');
      } finally {
        setSaving(false);
      }
    };
    if (confirmAction) {
      confirmAction('Delete Column', msg, doDelete);
    } else {
      if (window.confirm(msg)) doDelete();
    }
  };

  const moveColumn = async (group: NavGroup, direction: 'up' | 'down') => {
    const sorted = [...footerColumns].sort((a, b) => a.order - b.order);
    const idx = sorted.findIndex((g) => g.id === group.id);
    const swapIdx = direction === 'up' ? idx - 1 : idx + 1;
    if (swapIdx < 0 || swapIdx >= sorted.length) return;
    const ids = sorted.map((g) => g.id);
    [ids[idx], ids[swapIdx]] = [ids[swapIdx], ids[idx]];
    setSaving(true);
    try {
      await reorderNavGroups(ids, currentUserId);
      await load();
    } finally {
      setSaving(false);
    }
  };

  // ── HTML save ──────────────────────────────────────────────────────────────

  const handleSaveHtml = async (target: NavPlacement) => {
    setHtmlSaving(true);
    try {
      if (target === 'header') {
        await saveHeaderHtml(headerHtml || null, currentUserId);
        showNotify?.('Header HTML saved', 'success');
      } else {
        await saveFooterHtml(footerHtml || null, currentUserId);
        showNotify?.('Footer HTML saved', 'success');
      }
    } catch (err) {
      console.error('[AdminNavigation] Failed to save HTML:', err);
      showNotify?.('Failed to save HTML', 'error');
    } finally {
      setHtmlSaving(false);
    }
  };

  const handleClearHtml = async (target: NavPlacement) => {
    const label = target === 'header' ? 'header' : 'footer';
    const msg = `Clear the custom ${label} HTML? The default React ${label} will be shown instead.`;
    const doIt = async () => {
      setHtmlSaving(true);
      try {
        if (target === 'header') {
          await saveHeaderHtml(null, currentUserId);
          setHeaderHtml('');
        } else {
          await saveFooterHtml(null, currentUserId);
          setFooterHtml('');
        }
        showNotify?.(`Custom ${label} HTML cleared`, 'success');
      } catch (err) {
        console.error('[AdminNavigation] Failed to clear HTML:', err);
        showNotify?.('Failed to clear HTML', 'error');
      } finally {
        setHtmlSaving(false);
      }
    };
    if (confirmAction) {
      confirmAction(`Clear ${label} HTML`, msg, doIt);
    } else {
      if (window.confirm(msg)) doIt();
    }
  };

  // ── Nav item row ───────────────────────────────────────────────────────────

  const renderItem = (item: NavItem, siblings: NavItem[], isChild = false) => {
    const children = childrenOf(item.id);
    const sibIdx = siblings.findIndex((it) => it.id === item.id);
    const urlLabel = item.slug || item.url || '';
    return (
      <div key={item.id}>
        <div
          className={`flex items-center gap-2 py-2 px-3 rounded-lg border bg-white ${isChild ? 'ml-6 border-l-2 border-l-blue-200 border-gray-200' : 'border-gray-200'} ${!item.isVisible ? 'opacity-50' : ''}`}
        >
          <span className="shrink-0">
            {item.type === 'text' ? (
              <Type size={14} className="text-purple-400" />
            ) : item.type === 'html' ? (
              <FileCode size={14} className="text-amber-500" />
            ) : item.icon ? (
              <NavIconRenderer name={item.icon} size={15} className="text-blue-500" />
            ) : children.length > 0 ? (
              <ChevronRight size={14} className="text-gray-400" />
            ) : (
              <Link2 size={14} className="text-gray-400" />
            )}
          </span>
          <div className="flex-1 min-w-0">
            <span className="text-sm font-medium text-gray-800 truncate block">
              {item.type === 'html' ? <span className="italic text-amber-700">HTML snippet</span> : item.label}
            </span>
            {item.type === 'text' && (
              <span className="text-xs text-purple-500">text only</span>
            )}
            {item.type !== 'text' && item.type !== 'html' && urlLabel && (
              <span className="text-xs text-gray-400 truncate block">{urlLabel}</span>
            )}
          </div>
          <div className="flex items-center gap-1 shrink-0">
            <button type="button" onClick={() => moveItem(item, 'up')} disabled={saving || sibIdx === 0} className="p-1 rounded text-gray-400 hover:text-gray-700 hover:bg-gray-100 disabled:opacity-30" title="Move up"><ChevronUp size={13} /></button>
            <button type="button" onClick={() => moveItem(item, 'down')} disabled={saving || sibIdx === siblings.length - 1} className="p-1 rounded text-gray-400 hover:text-gray-700 hover:bg-gray-100 disabled:opacity-30" title="Move down"><ChevronDown size={13} /></button>
            <button type="button" onClick={() => toggleItemVisibility(item)} disabled={saving} className="p-1 rounded text-gray-400 hover:text-gray-700 hover:bg-gray-100 disabled:opacity-30" title={item.isVisible ? 'Hide' : 'Show'}>{item.isVisible ? <Eye size={13} /> : <EyeOff size={13} />}</button>
            {/* Add dropdown sub-item (only for top-level header items with no existing parent) */}
            {!isChild && placement === 'header' && (
              <button type="button" onClick={() => openAddItem(null, item.id)} className="p-1 rounded text-blue-500 hover:text-blue-700 hover:bg-blue-50" title="Add dropdown sub-item"><Plus size={13} /></button>
            )}
            <button type="button" onClick={() => openEditItem(item)} className="p-1 rounded text-gray-500 hover:text-blue-600 hover:bg-blue-50" title="Edit"><Pencil size={13} /></button>
            <button type="button" onClick={() => handleDeleteItem(item)} disabled={saving} className="p-1 rounded text-gray-400 hover:text-red-600 hover:bg-red-50 disabled:opacity-30" title="Delete"><Trash2 size={13} /></button>
          </div>
        </div>
        {children.length > 0 && (
          <div className="mt-1 space-y-1">
            {children.map((child) => renderItem(child, children, true))}
          </div>
        )}
      </div>
    );
  };

  // ── Nav item form modal ────────────────────────────────────────────────────

  const renderItemForm = () => {
    const isDropdownChild = itemFormParentId != null;
    const parentItem = isDropdownChild ? items.find((it) => it.id === itemFormParentId) : null;
    return (
      <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
        <div className="w-full max-w-md bg-white rounded-2xl shadow-2xl overflow-hidden">
          <div className="flex items-center justify-between bg-blue-600 px-5 py-4">
            <h3 className="text-base font-bold text-white">
              {editingItem ? 'Edit Link' : isDropdownChild ? `Add sub-item under "${parentItem?.label ?? ''}"` : 'Add Navigation Link'}
            </h3>
            <button type="button" onClick={() => setItemFormOpen(false)} className="text-blue-200 hover:text-white">
              <X size={18} />
            </button>
          </div>
          <div className="p-5 space-y-4">
            {/* Item type selector */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Item type</label>
              <div className="grid grid-cols-3 gap-2">
                <button
                  type="button"
                  onClick={() => setItemForm((f) => ({ ...f, itemType: 'link' }))}
                  className={`flex flex-col items-center gap-1 py-2.5 rounded-lg border text-xs font-medium transition-colors ${itemForm.itemType === 'link' ? 'border-blue-500 bg-blue-50 text-blue-700' : 'border-gray-200 text-gray-600 hover:border-blue-200 hover:bg-blue-50'}`}
                >
                  <Link2 size={15} />
                  Link
                </button>
                <button
                  type="button"
                  onClick={() => setItemForm((f) => ({ ...f, itemType: 'text' }))}
                  className={`flex flex-col items-center gap-1 py-2.5 rounded-lg border text-xs font-medium transition-colors ${itemForm.itemType === 'text' ? 'border-purple-500 bg-purple-50 text-purple-700' : 'border-gray-200 text-gray-600 hover:border-purple-200 hover:bg-purple-50'}`}
                >
                  <Type size={15} />
                  Text only
                </button>
                <button
                  type="button"
                  onClick={() => setItemForm((f) => ({ ...f, itemType: 'html' }))}
                  className={`flex flex-col items-center gap-1 py-2.5 rounded-lg border text-xs font-medium transition-colors ${itemForm.itemType === 'html' ? 'border-amber-500 bg-amber-50 text-amber-700' : 'border-gray-200 text-gray-600 hover:border-amber-200 hover:bg-amber-50'}`}
                >
                  <FileCode size={15} />
                  HTML snippet
                </button>
              </div>
            </div>

            {/* Label — for link and text types */}
            {itemForm.itemType !== 'html' && (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  {itemForm.itemType === 'text' ? 'Text content *' : 'Label *'}
                </label>
                {itemForm.itemType === 'text' ? (
                  <textarea
                    rows={3}
                    value={itemForm.label}
                    onChange={(e) => setItemForm((f) => ({ ...f, label: e.target.value }))}
                    placeholder="e.g. For questions about the platform, please contact the administrators."
                    autoFocus
                    className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-purple-500 resize-none"
                  />
                ) : (
                  <input
                    type="text"
                    value={itemForm.label}
                    onChange={(e) => setItemForm((f) => ({ ...f, label: e.target.value }))}
                    placeholder="e.g. About Us"
                    autoFocus
                    className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                )}
              </div>
            )}

            {/* HTML content — only for html type */}
            {itemForm.itemType === 'html' && (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">HTML content *</label>
                <textarea
                  rows={5}
                  value={itemForm.htmlContent}
                  onChange={(e) => setItemForm((f) => ({ ...f, htmlContent: e.target.value }))}
                  placeholder={'<p>For questions, call us:</p>\n<a href="tel:+18325426260">+1 832-542-6260</a>'}
                  autoFocus
                  className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm font-mono focus:outline-none focus:ring-2 focus:ring-amber-500 resize-none"
                />
                <p className="text-xs text-amber-700 mt-1">
                  This is the <strong>item snippet only</strong> — it appears inside this column on the footer, not the whole footer. Use <code className="bg-amber-100 px-1 rounded">{'{{phone-number}}'}</code> or <code className="bg-amber-100 px-1 rounded">{'{{contact-email}}'}</code> to pull values from Settings &amp; Branding.
                </p>
              </div>
            )}

            {/* URL destination — only for link type */}
            {itemForm.itemType === 'link' && (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Destination</label>
                <div className="grid grid-cols-2 gap-2 mb-3">
                  <button
                    type="button"
                    onClick={() => setItemForm((f) => ({ ...f, urlMode: 'cms' }))}
                    className={`flex items-center justify-center gap-1.5 py-2 rounded-lg border text-sm font-medium transition-colors ${itemForm.urlMode === 'cms' ? 'border-blue-500 bg-blue-50 text-blue-700' : 'border-gray-200 text-gray-600 hover:border-blue-200'}`}
                  >
                    <Link2 size={13} /> CMS Page
                  </button>
                  <button
                    type="button"
                    onClick={() => setItemForm((f) => ({ ...f, urlMode: 'custom' }))}
                    className={`flex items-center justify-center gap-1.5 py-2 rounded-lg border text-sm font-medium transition-colors ${itemForm.urlMode === 'custom' ? 'border-blue-500 bg-blue-50 text-blue-700' : 'border-gray-200 text-gray-600 hover:border-blue-200'}`}
                  >
                    <Code2 size={13} /> Custom URL
                  </button>
                </div>

                {itemForm.urlMode === 'cms' ? (
                  <div>
                    {cmsPages.length === 0 ? (
                      <p className="text-sm text-gray-500 italic">No published CMS pages found.</p>
                    ) : (
                      <select
                        value={itemForm.cmsSlug}
                        onChange={(e) => setItemForm((f) => ({ ...f, cmsSlug: e.target.value }))}
                        className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                      >
                        <option value="">— Select a page —</option>
                        {cmsPages.map((p) => (
                          <option key={p.id} value={p.slug}>
                            {p.title} ({p.slug})
                          </option>
                        ))}
                      </select>
                    )}
                    <p className="text-xs text-gray-400 mt-1">Links to a published CMS page by its slug.</p>
                  </div>
                ) : (
                  <div>
                    <input
                      type="text"
                      value={itemForm.customUrl}
                      onChange={(e) => setItemForm((f) => ({ ...f, customUrl: e.target.value }))}
                      placeholder="https://... or /internal-path"
                      className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                    />
                    <p className="text-xs text-gray-400 mt-1">Enter a full URL or internal path (e.g. /services).</p>
                  </div>
                )}
              </div>
            )}

            {/* Target — only for link type */}
            {itemForm.itemType === 'link' && (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Open in</label>
                <select
                  value={itemForm.target}
                  onChange={(e) => setItemForm((f) => ({ ...f, target: e.target.value as '_self' | '_blank' }))}
                  className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  <option value="_self">Same tab</option>
                  <option value="_blank">New tab</option>
                </select>
              </div>
            )}

            {/* Icon picker — link and text only */}
            {itemForm.itemType !== 'html' && <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Icon (optional)</label>
              <div className="grid grid-cols-8 gap-1 p-2 rounded-lg border border-gray-200 bg-gray-50 max-h-36 overflow-y-auto">
                {/* No icon option */}
                <button
                  type="button"
                  title="No icon"
                  onClick={() => setItemForm((f) => ({ ...f, icon: '' }))}
                  className={`flex items-center justify-center h-8 w-8 rounded-md border text-xs font-medium transition-colors ${!itemForm.icon ? 'border-blue-500 bg-blue-100 text-blue-700' : 'border-transparent text-gray-400 hover:bg-white hover:border-gray-300'}`}
                >
                  —
                </button>
                {ICON_NAMES.map((name) => (
                  <button
                    key={name}
                    type="button"
                    title={name}
                    onClick={() => setItemForm((f) => ({ ...f, icon: f.icon === name ? '' : name }))}
                    className={`flex items-center justify-center h-8 w-8 rounded-md border transition-colors ${itemForm.icon === name ? 'border-blue-500 bg-blue-100 text-blue-700' : 'border-transparent text-gray-500 hover:bg-white hover:border-gray-300 hover:text-gray-700'}`}
                  >
                    <NavIconRenderer name={name} size={15} />
                  </button>
                ))}
              </div>
              {itemForm.icon && (
                <p className="text-xs text-blue-600 mt-1 flex items-center gap-1">
                  <NavIconRenderer name={itemForm.icon} size={12} />
                  {itemForm.icon} selected
                </p>
              )}
            </div>}

            {/* Visibility */}
            <label className="flex items-center gap-2 cursor-pointer">
              <input
                type="checkbox"
                checked={itemForm.isVisible}
                onChange={(e) => setItemForm((f) => ({ ...f, isVisible: e.target.checked }))}
                className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
              />
              <span className="text-sm text-gray-700">Visible on site</span>
            </label>
          </div>
          <div className="flex justify-end gap-2 px-5 py-4 border-t border-gray-200 bg-gray-50">
            <button type="button" onClick={() => setItemFormOpen(false)} disabled={saving} className="px-4 py-2 text-sm font-medium text-gray-700 border border-gray-300 rounded-lg hover:bg-gray-100 disabled:opacity-60">
              Cancel
            </button>
            <button type="button" onClick={handleItemSave} disabled={saving} className="px-4 py-2 text-sm font-semibold text-white bg-blue-600 rounded-lg hover:bg-blue-700 disabled:opacity-60 inline-flex items-center gap-2">
              {saving && <Loader2 size={14} className="animate-spin" />}
              {editingItem ? 'Save changes' : 'Add item'}
            </button>
          </div>
        </div>
      </div>
    );
  };

  // ── Column form modal ──────────────────────────────────────────────────────

  const renderColumnForm = () => (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
      <div className="w-full max-w-sm bg-white rounded-2xl shadow-2xl overflow-hidden">
        <div className="flex items-center justify-between bg-blue-600 px-5 py-4">
          <h3 className="text-base font-bold text-white">
            {editingColumn ? 'Rename Column' : 'Add Footer Column'}
          </h3>
          <button type="button" onClick={() => setColumnFormOpen(false)} className="text-blue-200 hover:text-white">
            <X size={18} />
          </button>
        </div>
        <div className="p-5">
          <label className="block text-sm font-medium text-gray-700 mb-1">Column heading *</label>
          <input
            type="text"
            value={columnName}
            onChange={(e) => setColumnName(e.target.value)}
            placeholder="e.g. Company, Services, Resources"
            autoFocus
            onKeyDown={(e) => e.key === 'Enter' && handleColumnSave()}
            className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
          />
          <p className="text-xs text-gray-400 mt-1">Shown as the bold heading above the column's links in the footer.</p>
        </div>
        <div className="flex justify-end gap-2 px-5 py-4 border-t border-gray-200 bg-gray-50">
          <button type="button" onClick={() => setColumnFormOpen(false)} disabled={saving} className="px-4 py-2 text-sm font-medium text-gray-700 border border-gray-300 rounded-lg hover:bg-gray-100 disabled:opacity-60">
            Cancel
          </button>
          <button type="button" onClick={handleColumnSave} disabled={saving} className="px-4 py-2 text-sm font-semibold text-white bg-blue-600 rounded-lg hover:bg-blue-700 disabled:opacity-60 inline-flex items-center gap-2">
            {saving && <Loader2 size={14} className="animate-spin" />}
            {editingColumn ? 'Save' : 'Create column'}
          </button>
        </div>
      </div>
    </div>
  );

  // ── HTML editor section ────────────────────────────────────────────────────

  const renderHtmlEditor = (target: NavPlacement) => {
    const html = target === 'header' ? headerHtml : footerHtml;
    const setHtml = target === 'header' ? setHeaderHtml : setFooterHtml;
    const label = target === 'header' ? 'Header' : 'Footer';
    const isOpen = activeHtmlTab === target;

    return (
      <div className="bg-white rounded-2xl border border-gray-200 shadow-sm overflow-hidden">
        <button
          type="button"
          onClick={() => setActiveHtmlTab(isOpen ? null : target)}
          className="w-full flex items-center justify-between px-5 py-4 text-left hover:bg-gray-50 transition-colors"
        >
          <div className="flex items-center gap-3">
            <Code2 size={18} className="text-blue-500" />
            <div>
              <p className="text-sm font-semibold text-gray-800">Custom {label} HTML</p>
              <p className="text-xs text-gray-500">
                {html.trim() ? (
                  <span className="text-amber-600 font-medium">⚠ Active — overrides the default {label.toLowerCase()}</span>
                ) : (
                  `Optional. Write HTML to replace the default ${label.toLowerCase()} entirely.`
                )}
              </p>
            </div>
          </div>
          <ChevronRight size={16} className={`text-gray-400 transition-transform ${isOpen ? 'rotate-90' : ''}`} />
        </button>

        {isOpen && (
          <div className="border-t border-gray-200">
            <div className="px-5 py-3 bg-amber-50 border-b border-amber-200 flex items-start gap-2">
              <AlertTriangle size={15} className="text-amber-600 mt-0.5 shrink-0" />
              <p className="text-xs text-amber-700">
                If you write HTML here, it <strong>completely replaces</strong> the default React {label.toLowerCase()} on all public pages. Navigation links and items above (including HTML snippets in columns) will <strong>not</strong> be used. Leave blank to use the default {label.toLowerCase()} with your columns and items.
              </p>
            </div>
            <div className="px-5 py-3 border-b border-gray-100">
              <AdminCmsPlaceholderGuide
                title="Guide: embed codes"
                context="You can use {{phone-number}}, {{contact-email}}, {{company-address}}, {{brand-name}}, {{brand-tagline}} in this HTML; they are replaced with values from Settings & Branding."
                compact
              />
            </div>
            <div className="h-72">
              <Editor
                height="100%"
                language="html"
                value={html}
                onChange={(val) => setHtml(val ?? '')}
                options={{
                  minimap: { enabled: false },
                  fontSize: 13,
                  lineNumbers: 'on',
                  wordWrap: 'on',
                  scrollBeyondLastLine: false,
                  tabSize: 2,
                }}
                theme="vs-light"
              />
            </div>
            <div className="flex items-center justify-between px-5 py-3 border-t border-gray-200 bg-gray-50">
              <button
                type="button"
                onClick={() => handleClearHtml(target)}
                disabled={htmlSaving || !html.trim()}
                className="text-sm text-red-600 hover:text-red-800 disabled:opacity-40 font-medium"
              >
                Clear &amp; use default
              </button>
              <button
                type="button"
                onClick={() => handleSaveHtml(target)}
                disabled={htmlSaving}
                className="inline-flex items-center gap-2 px-4 py-2 bg-blue-600 text-white text-sm font-semibold rounded-xl hover:bg-blue-700 disabled:opacity-60"
              >
                {htmlSaving ? <Loader2 size={14} className="animate-spin" /> : <Save size={14} />}
                Save HTML
              </button>
            </div>
          </div>
        )}
      </div>
    );
  };

  // ── Main render ────────────────────────────────────────────────────────────

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <Loader2 size={32} className="animate-spin text-blue-500" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Page header */}
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Navigation Manager</h1>
          <p className="text-sm text-gray-500 mt-0.5">
            Manage header and footer navigation links. CMS page slugs are available as link destinations.
          </p>
        </div>
        {titleAction}
      </div>

      {/* Placement tabs */}
      <div className="flex gap-1 bg-gray-100 p-1 rounded-xl w-fit">
        {(['header', 'footer'] as NavPlacement[]).map((p) => (
          <button
            key={p}
            type="button"
            onClick={() => setPlacement(p)}
            className={`flex items-center gap-1.5 px-4 py-1.5 rounded-lg text-sm font-medium transition-colors capitalize ${
              placement === p ? 'bg-white text-gray-900 shadow-sm' : 'text-gray-600 hover:text-gray-900'
            }`}
          >
            {p === 'header' ? <LayoutTemplate size={14} /> : <Columns size={14} />}
            {p}
          </button>
        ))}
      </div>

      {/* ── HEADER TAB ─────────────────────────────────────────────────────── */}
      {placement === 'header' && (
        <div className="space-y-4">
          <div className="bg-white rounded-2xl border border-gray-200 shadow-sm overflow-hidden">
            <div className="flex items-center justify-between px-5 py-4 border-b border-gray-100">
              <div>
                <p className="text-sm font-semibold text-gray-800">Header Navigation Links</p>
                <p className="text-xs text-gray-500 mt-0.5">
                  Click <strong>+</strong> on any top-level item to add dropdown sub-items.
                </p>
              </div>
              <button
                type="button"
                onClick={() => openAddItem(null, null)}
                className="inline-flex items-center gap-1.5 px-3 py-2 bg-blue-600 text-white text-sm font-semibold rounded-xl hover:bg-blue-700 transition-colors"
              >
                <Plus size={14} />
                Add item
              </button>
            </div>
            <div className="p-4 space-y-2">
              {headerTopItems.length === 0 ? (
                <div className="text-center py-10 text-gray-400">
                  <Link2 size={32} className="mx-auto mb-2 text-gray-300" />
                  <p className="text-sm">No header links yet.</p>
                  <button
                    type="button"
                    onClick={() => openAddItem(null, null)}
                    className="mt-3 text-sm text-blue-600 hover:text-blue-800 font-medium inline-flex items-center gap-1"
                  >
                    <Plus size={13} /> Add your first item
                  </button>
                </div>
              ) : (
                headerTopItems.map((item) => renderItem(item, headerTopItems, false))
              )}
            </div>
          </div>

          {renderHtmlEditor('header')}
        </div>
      )}

      {/* ── FOOTER TAB ─────────────────────────────────────────────────────── */}
      {placement === 'footer' && (
        <div className="space-y-4">
          <div className="flex items-center justify-between">
            <p className="text-sm text-gray-500">
              Each column appears as a section in the footer. Add items (links, text, or HTML) to any column.
            </p>
            <button
              type="button"
              onClick={openAddColumn}
              className="inline-flex items-center gap-1.5 px-3 py-2 bg-blue-600 text-white text-sm font-semibold rounded-xl hover:bg-blue-700 transition-colors"
            >
              <Plus size={14} />
              Add column
            </button>
          </div>

          {footerColumns.length === 0 ? (
            <div className="text-center py-16 bg-white rounded-2xl border border-dashed border-gray-300">
              <Columns size={36} className="mx-auto text-gray-300 mb-3" />
              <p className="text-sm text-gray-500 font-medium">No footer columns yet</p>
              <button
                type="button"
                onClick={openAddColumn}
                className="mt-3 text-sm text-blue-600 hover:text-blue-800 font-medium inline-flex items-center gap-1"
              >
                <Plus size={13} /> Create first column
              </button>
            </div>
          ) : (
            <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
              {footerColumns.map((col, colIdx) => {
                const colItems = columnItems(col.id);
                return (
                  <div key={col.id} className="bg-white rounded-2xl border border-gray-200 shadow-sm overflow-hidden">
                    <div className="flex items-center gap-2 px-4 py-3 bg-gray-50 border-b border-gray-200">
                      <span className="flex-1 text-sm font-semibold text-gray-800 truncate">{col.name}</span>
                      <button type="button" onClick={() => moveColumn(col, 'up')} disabled={saving || colIdx === 0} className="p-1 rounded text-gray-400 hover:text-gray-700 hover:bg-gray-200 disabled:opacity-30" title="Move column left"><ChevronUp size={13} /></button>
                      <button type="button" onClick={() => moveColumn(col, 'down')} disabled={saving || colIdx === footerColumns.length - 1} className="p-1 rounded text-gray-400 hover:text-gray-700 hover:bg-gray-200 disabled:opacity-30" title="Move column right"><ChevronDown size={13} /></button>
                      <button type="button" onClick={() => openEditColumn(col)} className="p-1 rounded text-gray-500 hover:text-blue-600 hover:bg-blue-50" title="Rename column"><Pencil size={13} /></button>
                      <button type="button" onClick={() => handleDeleteColumn(col)} disabled={saving} className="p-1 rounded text-gray-400 hover:text-red-600 hover:bg-red-50 disabled:opacity-30" title="Delete column"><Trash2 size={13} /></button>
                    </div>
                    <div className="p-3 space-y-1.5">
                      {colItems.length === 0 && (
                        <p className="text-xs text-gray-400 text-center py-3">No links yet.</p>
                      )}
                      {colItems.map((item) => renderItem(item, colItems, false))}
                      <button
                        type="button"
                        onClick={() => openAddItem(col.id, null)}
                        className="mt-1 w-full flex items-center justify-center gap-1 py-1.5 text-xs font-medium text-blue-600 hover:text-blue-800 border border-dashed border-blue-200 rounded-lg hover:border-blue-400 transition-colors"
                      >
                        <Plus size={12} /> Add item
                      </button>
                    </div>
                  </div>
                );
              })}
            </div>
          )}

          {renderHtmlEditor('footer')}
        </div>
      )}

      {/* Modals */}
      {itemFormOpen && renderItemForm()}
      {columnFormOpen && renderColumnForm()}
    </div>
  );
};
